
const getPesaState = typeof window !== 'undefined' ? window.__PESA_GET_STATE__ : null;

export class PesaEngine {
  constructor(caseData, options = {}) {
    if (!caseData) throw new Error("PesaEngine needs a case");
    this.case = caseData;
    this.state = this._bootstrapState(caseData);
    this.history = [];
    this.counterfactuals = [];
    this.currentTick = 0;
    this._cache = new Map();
    this._timers = [];
    this.phaseTimer = null; 

    // SÉCURITÉ ABSOLUE – PLUS JAMAIS DE CRASH
    if (!this.state.completedActions || !(this.state.completedActions instanceof Set)) {
      this.state.completedActions = new Set();
    }
    if (!this.state.highlightedFamilies || !(this.state.highlightedFamilies instanceof Set)) {
      this.state.highlightedFamilies = new Set();
    }

    if (options.startTimer === true && this.state.currentPhase?.type !== 'action' && this.state.currentPhase?.duration > 0) {
    this._schedulePhaseAdvance(this.state.currentPhase.duration);
  }

    console.log("Engine initialized with case data:", caseData);    
  }

  _bootstrapState(data) {
    const src = data.initialState || data;
    const parameters = { ...(src.parameters || {}) };
    const initialParameters = { ...parameters };
    const families = (src.families || []).map(f => ({
      ...f,
      name: f.name,
      isActive: f.isActive ?? true,
      isToxic: f.isToxic ?? (f.type === "toxic"),
    }));
    

    const firstPhase = data.steps?.[0]?.phases?.[0]
      ? structuredClone(data.steps[0].phases[0])
      : null;
    

    return {
      parameters,
      initialParameters,
      families,
      agents: src.agents || data.agents || [],
      interactions: src.interactions || [],
      completedActions: new Set(src.completedActions || []),
      unlockedActions: new Set(),
      currentStepIndex: 0,
      currentPhaseIndex: 0,
      currentPhase: firstPhase,
      highlightedFamilies: new Set(),
      lastMicroEffects: [],
      microEffectsHistory: [],
      solutions: src.solutions || data.solutions || [],
      remainingTime: null,        // ← nouvelle propriété
      currentPhaseStart: null, 
      isPhaseRunning: false,
      showDebrief: false,
      showSolution: false,
      narrativeOverride: null,
    };
  }

  _schedulePhaseAdvance(duration) {
    if (this.phaseTimer) {
      clearTimeout(this.phaseTimer);
      this.phaseTimer = null;
    }

    // Initialisation du timer
    this.state.currentPhaseStart = Date.now();
    this.state.remainingTime = duration;

    // ✅ NOUVEAU : Throttle pour éviter trop de notifications
    let lastNotify = 0;

    const tick = () => {
      // Si la phase s'est terminée entre temps → on arrête
      if (!this.state.currentPhaseStart || this._advancing) return;

      const elapsed = (Date.now() - this.state.currentPhaseStart) / 1000;
      const remaining = duration - elapsed;

      this.state.remainingTime = Math.max(0, Math.ceil(remaining));

      // ✅ THROTTLE : Notifier max 1x par seconde (au lieu de 4x)
      const now = Date.now();
      if (now - lastNotify >= 1000) { // 1 seconde minimum entre chaque notification
        this._notifyStore();
        lastNotify = now;
      }

      // Si le temps n'est pas encore écoulé → on continue
      if (remaining > 0) {
        this.phaseTimer = setTimeout(tick, 250); // cadence stable et légère
        return;
      }

      // Sinon → fin du timer : on avance proprement la phase
      this.state.remainingTime = 0;
      this.phaseTimer = null;

      // advancePhase() a déjà un verrou anti-doublon
      this.advancePhase();
    };

    // Premier tick léger (démarrage immédiat)
    this.phaseTimer = setTimeout(tick, 50);
  }

  advancePhase() {
    // Empêche les doubles appels (tick + applyAction qui tombent en même temps)
    if (this._advancing) return;
    this._advancing = true;

    // 1. On coupe tout timer existant (propre et immédiat)
    if (this.phaseTimer) {
      clearTimeout(this.phaseTimer);
      this.phaseTimer = null;
    }

    // On stoppe la mesure du temps *mais sans notifier tout de suite*
    this.state.currentPhaseStart = null;
    this.state.remainingTime = null;

    const step = this.getCurrentStep();
    if (!step) {
      this._endSimulation();
      this._advancing = false;
      return;
    }

    const phases = step.phases || [];
    const nextPhaseIndex = this.state.currentPhaseIndex + 1;
    let newPhase = null;

    // 2. Passage à la phase suivante, ou au step suivant, ou fin
    if (nextPhaseIndex < phases.length) {
      // Phase suivante du même step
      this.state.currentPhaseIndex = nextPhaseIndex;
      newPhase = structuredClone(phases[nextPhaseIndex]);
    } else if (!this.isLastStep()) {
      // Step suivant
      this.state.currentStepIndex++;
      const nextStep = this.getCurrentStep();
      if (!nextStep) {
        // Fin
        this.state.currentPhase = null;
        this._endSimulation();
        this._advancing = false;
        return;
      }

      const nextPhases = nextStep.phases || [];
      this.state.currentPhaseIndex = 0;
      newPhase = nextPhases[0] ? structuredClone(nextPhases[0]) : null;
    } else {
      // Dernière phase du dernier step → fin totale
      this.state.currentPhase = null;
      this._endSimulation();
      this._advancing = false;
      return;
    }

    // 3. On met à jour la phase courante
    this.state.currentPhase = newPhase;

    // 4. On lance le timer *après avoir mis à jour la phase et l’état complet*
    if (newPhase?.type !== "action" && newPhase?.duration > 0) {
      this._schedulePhaseAdvance(newPhase.duration);
      this._stopDynamicsEngine(); // timer gère seul, pas de tick composé
    } else {
      this._startDynamicsEngine(); // live pendant que le joueur réfléchit
    }

    // 5. Notification unique du store (toutes données cohérentes)
    this._notifyStore();

    if (getPesaState) {
      const store = getPesaState();
      const caseId = this.case.slug || 'default';
      store.getPesaState(caseId);
    }
    

    // 6. Déblocage anti-double appel
    this._advancing = false;
  }

  _startDynamicsEngine() {
    // Si déjà lancé, on évite les doublons
    if (this._dynamicsInterval) return;

    const hasDynamics = this.case?.simulationEngine?.dynamics?.length > 0;
    if (!hasDynamics) return;

    // Lancer tick() toutes les 2 secondes pendant les phases narratives
    this._dynamicsInterval = setInterval(() => {
      // On ne tick que si on est dans une phase narrative (avec timer)
      if (this.state.currentPhase?.type !== 'action' && this.state.currentPhase?.duration > 0) {
        this.tick();
      }
    }, 2000); // 2 secondes
  }

  _stopDynamicsEngine() {
    if (this._dynamicsInterval) {
      clearInterval(this._dynamicsInterval);
      this._dynamicsInterval = null;
    }
  }

  _endSimulation() {
    // 1. Détection de la solution atteinte (exacte ou la plus proche)
    this._detectCurrentSolution(); // ← garde cette méthode si elle est déjà générique

    // Si pas de match exact → détection d’un parcours hybride/imprévu
    if (!this.state.currentSolution || this.state.currentSolution.matchType !== 'exact') {
      this._detectLivingPath(); // ← à rendre générique aussi (voir ci-dessous)
    }

    // 2. Calibration features → générée dynamiquement depuis le seed
    this.state.calibrationFeatures = this._generateCalibrationFeatures();

    // 3. Contrefactuels (déjà corrigé dans le message précédent)
    this.counterfactuals = this.generateCounterfactuals();

    // 4. Fin de simulation
    this.state.showDebrief = false;

    // 5. Notification finale
    this._notifyStore();

    if (getPesaState) {
      const store = getPesaState();
      const caseId = this.case.slug || 'default';
      store.getPesaState(caseId);
    }
  }

  _updateCurrentSolution() {
    const solution = this.case.solutions?.find(sol =>
      sol.requiredActions.length > 0 &&
      sol.requiredActions.every(id => this.state.completedActions.has(id.trim()))
    );

    if (solution) {
      this.state.currentSolution = solution;
      // Préparation features pour calibration (même si pas encore fin)
      this.state.calibrationFeatures = this._generateCalibrationFeatures();
    } else {
      this.state.currentSolution = null;
    }
  }

  _detectCurrentSolution() {
    const completed = this.state.completedActions;

    // Recherche exacte
    const exact = this.case.solutions?.find(sol =>
      sol.requiredActions?.every(id => completed.has(id.trim()))
    );

    if (exact) {
      this.state.currentSolution = {
        ...exact,
        matchType: "exact"
      };
      return;
    }

    // Si pas exact → on laisse _detectLivingPath gérer le cas "imprévu"
    this.state.currentSolution = null;
  }

  _detectLivingPath() {
    const completed = this.state.completedActions;
    const score = this.state.emancipationScore || this.getEmancipationScore();

    // Recherche la solution la plus proche (même si pas exacte)
    let closestSolution = null;
    let maxMatchRatio = 0;

    this.case.solutions.forEach(sol => {
      if (sol.requiredActions.length === 0) return; // on ignore les defaults vides

      const common = sol.requiredActions.filter(id => completed.has(id.trim())).length;
      const ratio = common / sol.requiredActions.length;

      if (ratio > maxMatchRatio) {
        maxMatchRatio = ratio;
        closestSolution = sol;
      }
    });

    const theoreticalPromise = closestSolution?.interpretation.theoreticalPromise || 50;
    const realProbability = this.state.currentSolution?.interpretation.realWorldProbability || 50;

    const delta = score - theoreticalPromise;

    // Si écart significatif ou match faible → parcours hybride
    if (maxMatchRatio < 0.5 || Math.abs(delta) > 10) {
      this.state.currentSolution = {
        name: "Parcours hybride imprévu",
        matchType: "hybrid",
        interpretation: {
          shortSummary: delta > 0 
            ? "Le vivant a dépassé les modèles théoriques."
            : "Le vivant a résisté plus que prévu à la transformation.",
          theoreticalPromise: theoreticalPromise,
          realWorldProbability: realProbability,
          tone: "lucide",
          keyInsight: delta > 0 
            ? "Le réel produit parfois plus d’émancipation que la théorie ne le permet."
            : "La reproduction est plus résiliente que prévu, même avec des tentatives de subversion.",
          pedagogicalMessage: "Les chemins réels sont toujours plus complexes que les modèles. C’est dans ces écarts que naît la vraie compréhension.",
          deltaExplanation: delta > 0 
            ? `Dépassement exceptionnel du vivant : +${Math.abs(delta)} points`
            : `Résistance inattendue du système : ${Math.abs(delta)} points de moins`,
          hackerDetection: completed.size === 0 
            ? "Vous n’avez rien fait. Le système a suivi sa logique propre."
            : completed.size > 10 
              ? "Vous avez exploré un espace incohérent, forçant le vivant à inventer sa propre réponse."
              : "Vos choix partiels ou contradictoires ont créé une trajectoire que la théorie n’avait pas anticipée."
        }
      };
    }
  }
  
  isSimulationFinished() {
    if (!this.case?.steps?.length) return false;

    const lastStepIndex = this.case.steps.length - 1;
    const lastStep = this.case.steps[lastStepIndex];
    if (!lastStep?.phases?.length) {
      return this.state.currentStepIndex >= lastStepIndex;
    }

    const lastPhaseIndex = lastStep.phases.length - 1;

    // On est fini dès qu’on a DÉPASSÉ ou qu’on est sur la dernière phase du dernier step
    return (
      this.state.currentStepIndex > lastStepIndex ||
      (this.state.currentStepIndex === lastStepIndex &&
        this.state.currentPhaseIndex >= lastPhaseIndex)
    );
  }

  applyAction(actionId) {
    const step = this.getCurrentStep();
    if (!step) return { success: false };

    const action = step.actions.find(a => a.id === actionId);
    if (!action || this.state.completedActions.has(actionId)) return { success: false };

    const nextState = { ...this.state };
    
    if (action.effect) {
        Object.entries(action.effect).forEach(([k, v]) => {
            nextState.parameters[k] = Math.max(0, Math.min(100, (nextState.parameters[k] || 50) + v));
        });
    }

    if (action.toggleFamilies?.length) {
      this.state.families = this.state.families.map(f => {
        if (!action.toggleFamilies.includes(f.name)) return f;
        return { ...f, isActive: f.name === "Art" || f.name === "Médiation" ? true : !f.isActive };
      });
    }

    if (action.addInteractions?.length) {
      action.addInteractions.forEach(link => {
        const exists = this.state.interactions.some(i =>
          (i.source === link.source && i.target === link.target) ||
          (i.source === link.target && i.target === link.source)
        );
        if (!exists) this.state.interactions.push({ ...link });
      });
    }

    if (action.highlightFamilies?.length) {
      this.state.highlightedFamilies = new Set(action.highlightFamilies);
      setTimeout(() => this.state.highlightedFamilies.clear(), 6000);
    }

    if (action.microEffects?.length) {
        action.microEffects.forEach(me => {
            const agent = nextState.agents.find(a => a.name === me.target || a._id === me.target);
            if (agent) {
                if (agent.resources instanceof Map) {
                    const current = agent.resources.get(me.variable) || 50;
                    agent.resources.set(me.variable, Math.max(0, Math.min(100, current + me.delta)));
                } else {
                    if (!agent.resources) agent.resources = {};
                    const current = agent.resources[me.variable] || 50;
                    agent.resources[me.variable] = Math.max(0, Math.min(100, current + me.delta));
                }
            }
        });
    }  
    
    this.state = nextState;
    this.state.completedActions.add(actionId);
    this._log("action", { actionId, timestamp: Date.now(), tick: this.currentTick });
    this._triggerAutoEvents("afterAction");

    this._detectCurrentSolution();
    this._updateCurrentSolution();
    
    // ✅ NOUVEAU : Ne plus appeler tick() systématiquement
    // On appelle tick() SEULEMENT si c'est une phase action (pas de timer automatique)
    // Pendant les phases narratives avec timer, c'est le dynamics engine qui tourne via le scheduler
    if (!this._dynamicsInterval) {
    this.tick();
  }

    this.counterfactuals = this.generateCounterfactuals();

    // === GESTION DE LA PROGRESSION ===
    const currentPhase = this.state.currentPhase;

    // Si la phase a un timer > 0 → on ne fait rien, le timer avance seul
    if (currentPhase?.duration > 0) {
      this._notifyStore();
      return { success: true };
    }

    // Si la phase est de type action et qu'il reste des actions disponibles → on attend "Continuer"
    const availableInPhase = currentPhase?.actionIds?.filter(id => !this.state.completedActions.has(id)) || [];

    if (currentPhase?.type === "action" && availableInPhase.length > 0) {
      // Il reste des choix → on notifie mais on n'avance pas
      this._notifyStore();
      return { success: true };
    }

    // Sinon : plus d'actions dans la phase → on avance automatiquement
    if (this.isStepCompleted()) {
      if (this.isLastStep()) {
        this._endSimulation();
      } else {
        this.nextStep();
        this._triggerAutoEvents("afterStep");
      }
    }

    this.advancePhase();

    this._notifyStore();

    if (getPesaState) {
      const store = getPesaState();
      const caseId = this.case.slug || 'default';
      store.joinPesaCase(caseId);
      store.getPesaState(caseId);
    }

    return { success: true };
  }

  nextStep() {
    // 1. On ne passe au step suivant QUE si on est sur le dernier step du courant
    // ET que toutes ses actions sont complétées (ou qu'il n'en a pas)
    const currentStep = this.getCurrentStep();
    if (!currentStep) return false;

    const hasActions = currentStep.actions && currentStep.actions.length > 0;
    const allActionsDone = hasActions 
      ? currentStep.actions.every(a => this.state.completedActions.has(a.id))
      : true;

    if (!allActionsDone) {
      // On refuse de passer tant que le joueur n'a pas fait toutes les actions du step courant
      return false;
    }

    // 2. On est prêt à passer au step suivant
    if (this.isLastStep()) {
      return false; // on était déjà au dernier
    }

    this.state.currentStepIndex++;

    // 3. Setup du nouveau step
    const nextStep = this.getCurrentStep();
    if (!nextStep) {
      // On a dépassé → c'est la fin
      this.state.currentPhase = null;
      this.state.currentPhaseIndex = 0;
      if (window.__PESA_STORE__) {
        window.__PESA_STORE__.setState({ phase: 'finished' });
      }
      this._notifyStore();
      return true;
    }

    const nextPhases = nextStep.phases || [];
    this.state.currentPhaseIndex = 0;
    this.state.currentPhase = nextPhases[0] ? structuredClone(nextPhases[0]) : null;

    if (
      this.state.currentPhase?.type !== 'action' &&
      this.state.currentPhase?.duration > 0
    ) {
      this._schedulePhaseAdvance(this.state.currentPhase.duration);
    }

    this._notifyStore();
    return true;
  }

  goToStep(index) {
    if (index >= 0 && index < this.case.steps.length) {
      this.state.currentStepIndex = index;
      return true;
    }
    return false;
  }

  tick() {
    // 1. Calcul des indicateurs dérivés (nouveau système)
    const derived = this.evaluateComputed(this.case.simulationEngine?.computedMetrics || {});

    // 2. Contexte complet : parameters + derived
    let fullContext = {
      ...this.state.parameters,
      ...derived
    };

    // 3. Compatibilité legacy
    if (Object.keys(derived).length === 0) {
      const familles_actives = this.state.families.filter(f => f.isActive).map(f => f.name);
      const familles_toxiques = this.state.families.filter(f => f.isActive && f.isToxic).length;
      fullContext = { ...fullContext, familles_actives, familles_toxiques };
    }

    const dynamics = this.case?.simulationEngine?.dynamics || [];
    if (dynamics.length === 0) return;

    const baseParams = { ...this.state.parameters };
    const newParams = { ...baseParams };
    let changed = false;

    for (const dyn of dynamics) {
      if (!dyn.formula || !(dyn.parameter in newParams)) continue;

      try {
        let result = this.evaluate(dyn.formula, dyn.formula, fullContext);

        // Protection contre NaN / Infinity
        if (!isFinite(result) || isNaN(result)) {
          console.warn(`Resultat invalide pour ${dyn.parameter}: ${result} → ignoré`);
          continue;
        }

        const clamped = Math.max(0, Math.min(100, Math.round(result)));
        
        // ✅ NOUVEAU : Seuil plus élevé pour réduire les micro-changements
        // On ne met à jour que si delta >= 1 (au lieu de 0.1)
        if (Math.abs(clamped - newParams[dyn.parameter]) >= 1) {
          newParams[dyn.parameter] = clamped;
          changed = true;
        }
      } catch (err) {
        console.warn("Formule plantée → ignorée :", dyn.parameter, dyn.formula, err.message);
      }
    }

    if (changed) {
      this.state.parameters = newParams;
    }

    this.currentTick++;
    //this.counterfactuals = this.generateCounterfactuals();

    // ✅ NOUVEAU : Ne notifier que si quelque chose a vraiment changé
    if (changed) {
      this._notifyStore();
    }

    if (getPesaState && changed) {
      const store = getPesaState();
      const caseId = this.case.slug || 'default';
      store.getPesaState(caseId);
    }
  }

  generateCounterfactuals() {
    const currentPath = [...this.state.completedActions];
    const allActions = this.case.steps.flatMap(s => s.actions.map(a => a.id));
    const solutions = this.case.solutions || [];
    const counterfactuals = [];

    // 1. Pour chaque action manquante dans le chemin actuel
    for (const missingId of allActions) {
      if (currentPath.includes(missingId)) continue;

      const action = this.case.steps
        .flatMap(s => s.actions)
        .find(a => a.id === missingId);

      if (!action) continue;

      // 2. Calcul générique du delta : combien cette action rapproche-t-on des solutions ?
      let delta = 0;
      let matchedSolutions = 0;

      for (const sol of solutions) {
        if (!sol.requiredActions?.length) continue;

        const missingForThisSol = sol.requiredActions.filter(id => !currentPath.includes(id));
        if (missingForThisSol.includes(missingId)) {
          // Cette action manquante est requise par cette solution
          matchedSolutions++;
          // Bonus proportionnel : plus il reste peu d'actions, plus l'impact est fort
          const remainingRatio = missingForThisSol.length / sol.requiredActions.length;
          delta += (1 - remainingRatio) * 0.5; // max +0.5 par solution
        }
      }

      // Bonus si l'action a un gros effet positif sur des métriques
      if (action.effect) {
        const totalBoost = Object.values(action.effect).reduce((sum, v) => sum + (v > 0 ? v : 0), 0);
        delta += Math.min(0.3, totalBoost / 100); // max +0.3
      }

      // 3. Si delta significatif → on l'ajoute
      if (delta > 0.15) {
        counterfactuals.push({
          actionId: action.id,
          actionTitle: action.label,
          deltaProbability: delta,
          description: `Cette action aurait augmenté la probabilité d'atteindre une solution optimale de ${Math.round(delta * 100)} %.`,
          targetParameter: action.effect ? Object.keys(action.effect).find(k => action.effect[k] > 0) : null,
          matchedSolutions
        });
      }
    }   

    return counterfactuals;
  }

  _generateCalibrationFeatures() {
    const features = {};

    // Option 1 : le seed peut déclarer explicitement ses features dans metadata ou un nouveau champ
    if (this.case.metadata?.calibrationFeatures) {
      Object.assign(features, this.case.metadata.calibrationFeatures);
    }

    // Option 2 : calcul générique basé sur les solutions et actions complétées
    this.case.solutions?.forEach(sol => {
      if (!sol.requiredActions?.length) return;

      sol.requiredActions.forEach(actionId => {
        const action = this._findActionById(actionId);
        if (!action) return;

        // Exemple générique : on regarde les effets positifs forts
        if (action.effect) {
          Object.entries(action.effect).forEach(([param, delta]) => {
            if (delta > 20) { // seuil arbitraire pour "impact significatif"
              features[`highImpact_${param}`] = this.state.completedActions.has(actionId);
            }
          });
        }

        // Bonus si l'action toggle des familles importantes (générique)
        if (action.toggleFamilies?.length) {
          action.toggleFamilies.forEach(familyName => {
            features[`familyActivated_${familyName}`] = this.state.completedActions.has(actionId);
          });
        }
      });
    });

    return features;
  }

  _findActionById(id) {
    return this.case.steps
      .flatMap(s => s.actions)
      .find(a => a.id === id);
  }

  _triggerAutoEvents(triggerType) {
    const step = this.getCurrentStep();
    if (!step?.autoEvents) return;

    step.autoEvents.forEach(event => {
      if (event.trigger && !["always", triggerType].includes(event.trigger) && event.trigger !== "threshold") return;

      if (event.trigger === "threshold") {
        const { parameter, operator = ">", threshold } = event.condition || {};
        const value = this.state.parameters[parameter] ?? 50;
        const met =
          operator === ">" ? value > threshold :
          operator === ">=" ? value >= threshold :
          operator === "<" ? value < threshold :
          operator === "<=" ? value <= threshold : false;
        if (!met) return;
      }

      // Condition ?
      if (event.condition) {
        const { parameter, operator = ">", threshold } = event.condition;
        const value = this.state.parameters[parameter] ?? 50;
        const met =
          operator === ">" ? value > threshold :
          operator === ">=" ? value >= threshold :
          operator === "<" ? value < threshold :
          operator === "<=" ? value <= threshold : false;
        if (!met) return;
      }

      // Effet
      if (event.effect) {
        Object.entries(event.effect).forEach(([k, v]) => {
          this.state.parameters[k] = Math.max(0, Math.min(100, (this.state.parameters[k] || 50) + v));
        });
      }

      if (event.description) {
        this._log("auto-event", event.description);
      }
    });
  }

  _triggerPhaseEvents(phase) {
    if (!phase) return;

    // 1. AutoEvents
    if (phase.autoEvents?.length) {
      phase.autoEvents.forEach(event => {
        if (event.effect) {
          Object.entries(event.effect).forEach(([k, v]) => {
            this.state.parameters[k] = Math.max(0, Math.min(100, (this.state.parameters[k] || 50) + v));
          });
        }
      });
    }

    // 2. MicroEffects avec delay
    if (phase.microEffects?.length) {
      phase.microEffects.forEach(me => {
        const delay = (me.delay || 0) * 1000;
        setTimeout(() => {
          this.state.lastMicroEffects = [{
            target: me.target,
            variable: me.variable,
            delta: me.delta,
            appliedAt: this.currentTick
          }];
          if (!this.state.microEffectsHistory) this.state.microEffectsHistory = [];
          this.state.microEffectsHistory.push(...this.state.lastMicroEffects);
        }, delay);
      });
    }
  }

  evaluateComputed(computed = {}) {
    const result = {};

    // ✅ FIX : Contexte étendu avec accès direct aux propriétés
    const safeContext = {
      // Propriétés directes (pour compatibilité avec anciennes expressions)
      parameters: { ...this.state.parameters },
      interactions: [...this.state.interactions],
      completedActions: [...this.state.completedActions],
      families: [...this.state.families],
      
      // ✅ NOUVEAU : Ajouter les paramètres directement au contexte
      ...this.state.parameters,
      
      // ✅ NOUVEAU : Variables helper pour compatibilité legacy
      familles_actives: this.state.families.filter(f => f.isActive).map(f => f.name),
      familles_toxiques: this.state.families.filter(f => f.isActive && f.isToxic).length,
      
      // Objets helpers
      Math,
      state: {
        parameters: { ...this.state.parameters },
        interactions: [...this.state.interactions],
        completedActions: [...this.state.completedActions],
        families: [...this.state.families]
      }
    };

    for (const [key, expr] of Object.entries(computed)) {
      try {
        // ✅ FIX : Passer TOUS les paramètres du safeContext comme arguments
        const params = Object.keys(safeContext);
        const values = Object.values(safeContext);
        
        const fn = new Function(...params, `"use strict"; return (${expr})`);
        const value = fn(...values);

        // Si c'est un nombre, on clamp. Sinon, on garde tel quel.
        if (typeof value === 'number') {
          result[key] = Math.max(0, Math.min(100, value));
        } else {
          result[key] = value; 
        }
      } catch (e) {
        console.warn(`[evaluateComputed] Erreur pour ${key}:`, expr, e.message);
        result[key] = 0;
      }
    }
    return result;
  }

  evaluate(expr, formula, extraContext = {}) {
    if (!expr || typeof expr !== "string") return 1;

    const cacheKey = expr + JSON.stringify(extraContext);
    if (this._cache.has(cacheKey)) return this._cache.get(cacheKey);

    const allowed = {
      ...this.state.parameters,
      ...extraContext,
      Math, abs: Math.abs, round: Math.round, floor: Math.floor, ceil: Math.ceil,
      min: Math.min, max: Math.max,
      includes: Array.prototype.includes,
      filter: Array.prototype.filter,
      some: Array.prototype.some,
      length: 'length'
    };

    let sanitized = expr.trim();

    // 1. Protéger toutes les strings littérales (simple et double quotes)
    const protectedStrings = [];
    sanitized = sanitized.replace(/(['"])(?:(?=(\\?))\2.)*?\1/g, (match) => {
      protectedStrings.push(match);
      return `__STRING_${protectedStrings.length - 1}__`;
    });

    // 2. Remplacer les identifiants inconnus (variables/fonctions) - maintenant hors strings
    sanitized = sanitized.replace(/(?<![\w.])([a-zA-Z_][a-zA-Z0-9_]*)(?![\w.])/g, m => {
      if (['includes', 'filter', 'some', 'length', 'Math', 'abs', 'round', 'min', 'max'].includes(m)) return m;
      return Object.hasOwn(allowed, m) ? m : "0";
    });

    // 3. Restaurer les strings protégées
    sanitized = sanitized.replace(/__STRING_(\d+)__/g, (_, i) => protectedStrings[parseInt(i)] || '');

    

    // Vérification sécurité syntaxe (tolérante aux strings)
    const sanitizedTest = sanitized.replace(/\b\w+\b/g, '0');
    if (!/^[\d+\-*/().%<>!=&|?: ]+$/.test(sanitizedTest)) {
      console.warn("Syntaxe invalide après sanitization:", sanitized);
      this._cache.set(cacheKey, 1);
      return 1;
    }

    try {
      const fn = new Function(...Object.keys(allowed), `"use strict"; return ${sanitized}`);
      const result = fn(...Object.values(allowed));
      this._cache.set(cacheKey, result);
      return Number(result) || 0;
    } catch (e) {
      console.warn("PESA eval error →", expr, sanitized, e.message);
      this._cache.set(cacheKey, 1);
      return 1;
    }
  }

  serialize() {
    return {
      tick: this.currentTick,
      state: {
        parameters: { ...this.state.parameters },
        families: this.state.families.map(f => ({ ...f })),
        agents: this.state.agents.map(a => ({ ...a })),
        interactions: [...this.state.interactions],
        completedActions: [...this.state.completedActions],
        currentStepIndex: this.state.currentStepIndex,
      },
      history: [...this.history],
    };
  }

  hydrate(serialized) {
    Object.assign(this, serialized);
    this.state.completedActions = new Set(this.state.completedActions);
    this.state.highlightedFamilies = new Set(this.state.highlightedFamilies || []);
  }

  reset() {
    this.state = this._bootstrapState(this.case);
    this.history = [];
    this.currentTick = 0;
    this._cache.clear();
  }

  isStepCompleted() {
    const step = this.getCurrentStep();
    if (!step) return true;
    return step.actions.every(a => this.state.completedActions.has(a.id));
  }

  isLastStep() {
    return this.state.currentStepIndex >= this.case.steps.length - 1;
  }

  getCurrentStep () {
    const step = this.case.steps[this.state.currentStepIndex];
    return step || null;
  }

  unlockedActions() {
    const unlocked = new Set();

    for (let i = 0; i <= this.state.currentStepIndex; i++) {
      const step = this.case.steps[i];
      if (!step?.actions) continue;

      step.actions.forEach(action => {
        const prereqs = action.prerequisites || [];
        const allPrereqsMet = prereqs.length === 0 || 
          prereqs.every(preId => this.state.completedActions.has(preId.trim()));

        if (allPrereqsMet) {
          unlocked.add(action.id);
        }
      });
    }

    return unlocked;
  }

  _notifyStore() {
  if (typeof window === "undefined" || !window.__PESA_STORE__) return;

  const finished = this.isSimulationFinished();

  window.__PESA_STORE__.setState({
    currentCase: this.case,
    currentPhase: this.state.currentPhase,
    currentPhaseIndex: this.state.currentPhaseIndex,
    currentStep: this.getCurrentStep(),
    currentStepIndex: this.state.currentStepIndex,
    parameters: { ...this.state.parameters }, 
    initialParameters: { ...this.state.initialParameters }, 
    families: [...this.state.families.map(f => ({ ...f }))], 
    completedActions: new Set(this.state.completedActions), 
    unlockedActions: this.unlockedActions(),
    phase: finished ? 'finished' : 'playing',
    showDebrief: finished,
    remainingTime: this.state.remainingTime,
    currentSolution: this.case.solutions?.find(sol =>
      sol.requiredActions.every(id => this.state.completedActions.has(id.trim()))
    ) || null,
  });
}

  _log(type, data) {
    this.history.push({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      tick: this.currentTick,
    });

  } 
}